import { forwardRef, type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Autocomplete, Button, IconButton, Input, Popover, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';
import { clsx } from 'clsx';

import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';
import type { FieldOptions, FilterToken, Operator, ParsedToken } from '../../../types/QueryBuilder';
import type { AutocompleteOption } from '../AutocompletePopover';
import { AutocompletePopover } from '../AutocompletePopover';
import { useQueryBuilderContext } from '../context';
import { FieldValuePanel } from '../fields/FieldValuePanel';
import { buildButtonLabelParts } from '../fields/FieldValueSearch';
import { useFilterBarContext } from '../FilterBarContext';
import type { FieldSampleValue } from '../FilterBarContext';
import { useAutocompleteNavigation } from '../hooks/useAutocompleteNavigation';
import { useInputWidthMirror } from '../hooks/useInputWidthMirror';
import { useQueryBuilderGridItem } from '../hooks/useQueryBuilderGridItem';
import {
  bestMatchIndex,
  compareOptionMatch,
  dedupeOptionsByValue,
  ensureBooleanSampleValues,
  EXISTS_OPERATOR_OPTION,
  expressionTypeAtOffset,
  fieldAllowsMultipleValues,
  fieldOptionsForScope,
  getDefaultOperator,
  getFocusableCells,
  getOperatorCellDisplay,
  inferFieldType,
  isDurationValueInput,
  isExistsOperatorOption,
  isOperatorCompatible,
  matchScore,
  NOT_EXISTS_OPERATOR_OPTION,
  OPERATOR_OPTIONS,
  replaceToken,
  scopeForExpressionType,
  tokenValueTexts,
  withExistsOperatorOptions,
} from '../utils';

import styles from './LozengeFilter.module.css';

/** Sentinel for the pinned "Search for value:" option in the value autocomplete. */
const SEARCH_VALUE_SENTINEL = '__SEARCH_VALUE__';

/**
 * Case-insensitive substring match of an autocomplete option against the typed text.
 * Mirrors the field-key autocomplete in FreeTextToken so suggestions are *filtered*
 * as the user types (not just highlighted). Matches on the raw value and on the
 * human-readable displayName. Used by both the key and value cells.
 */
function optionMatchesSearch(
  option: { value: string | number | boolean; displayName?: string },
  search: string,
): boolean {
  const query = search.toLowerCase();
  if (String(option.value).toLowerCase().includes(query)) {
    return true;
  }
  return (option.displayName ?? '').toLowerCase().includes(query);
}

/** Splits a comma-separated multi-value string into individual trimmed values. */
function parseMultiValue(str: string): string[] {
  return str
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

/** Wraps a token value in an array, passing an already-array value through unchanged. */
function toValueArray(
  value: string | number | boolean | Array<string | number | boolean>,
): Array<string | number | boolean> {
  return Array.isArray(value) ? value : [value];
}

function compileFilterRegex(pattern: string): RegExp | null {
  if (!pattern) {
    return null;
  }
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
}

/**
 * The inline variant's current values, read from whichever value source the lozenge was
 * given: the parsed token when present, else the `values` array, else the scalar `value`.
 * An unset value yields an empty array (a pinned field with no token yet).
 */
function inlineValuesFor(
  value: FilterToken,
  parsedToken?: Extract<ParsedToken, { type: 'FILTER' }>,
): Array<string | number | boolean> {
  if (parsedToken) {
    return tokenValueTexts(parsedToken);
  }
  if ('values' in value && value.values) {
    return value.values;
  }
  if (value.value !== undefined) {
    return toValueArray(value.value);
  }
  return [];
}

/**
 * Inline boolean chips toggle on press, so the tooltip names that action even when a
 * value is selected. Other chips may truncate the trigger, so they show the full selection.
 */
export function inlineValueTooltip(isBooleanField: boolean, displayValues: Array<string | number | boolean>): string {
  if (isBooleanField) {
    return msg('queryBuilder.filterToken.toggleFilter');
  }
  if (displayValues.length === 0) {
    return msg('queryBuilder.filterToken.editFilter');
  }
  if (displayValues.length === 1) {
    return String(displayValues[0]);
  }
  return msg('queryBuilder.filterToken.moreValues', {
    first: String(displayValues[0]),
    count: displayValues.length - 1,
  });
}

/**
 * Returns the comma-delimited segment that the cursor currently sits within.
 * For example, given "foo, bar|, baz" (| = cursor at offset 8), returns "bar".
 */
function getSegmentAtCursor(text: string, cursorPos: number): string {
  const before = text.slice(0, cursorPos);
  const segStart = before.lastIndexOf(',') + 1;
  const afterCursor = text.slice(cursorPos);
  const commaAfter = afterCursor.indexOf(',');
  const segEnd = commaAfter === -1 ? text.length : cursorPos + commaAfter;
  return text.slice(segStart, segEnd).trim();
}

/**
 * Position of the cursor's comma segment within the value array `parseMultiValue` builds
 * from the same text — i.e. how many non-empty segments precede it. Returns -1 when the
 * cursor sits in an empty segment (e.g. just after the trailing ", "), which contributes
 * no value. Pairs with getSegmentAtCursor so the segment being typed can be located in
 * the committed values.
 */
function getSegmentIndexAtCursor(text: string, cursorPos: number): number {
  if (getSegmentAtCursor(text, cursorPos) === '') {
    return -1;
  }
  const segStart = text.slice(0, cursorPos).lastIndexOf(',') + 1;
  return parseMultiValue(text.slice(0, segStart)).length;
}

// True when the user's last pointerdown landed outside both the value input's filter bar (its
// nearest [role="grid"]) and the popover listbox — i.e. a genuine click-away. A null target (no
// pointer interaction: keyboard nav, or a purely programmatic focus move) counts as "inside" so
// react-aria's own focus restoration is still treated as a steal. When there's no resolvable
// filter-bar boundary we can't be sure, so we fall back to "inside" (preserve steal behavior).
function pointerLandedOutsideCombobox(
  target: EventTarget | null,
  input: HTMLInputElement,
  listbox: HTMLElement | null,
): boolean {
  if (!(target instanceof Node)) {
    return false;
  }
  if (listbox?.contains(target)) {
    return false;
  }
  const grid = input.closest('[role="grid"]');
  if (!grid) {
    return false;
  }
  return !grid.contains(target);
}

// A focus target that means react-aria stole focus from the open value popover rather than
// the user leaving: one of the popover's own options, or nothing/body (react-aria restoring
// the closing FreeText popover lands on the body since its trigger is aria-hidden).
//
// A null/body target is ambiguous — react-aria's restoration lands there too — so we
// disambiguate with the user's most recent pointerdown: if they just pointed at a non-focusable
// area *outside* the combobox and its popover, that's a real dismissal (return false so the cell
// deactivates), not a steal.
//
// Pointerdown on another lozenge cell (key/op/value) inside the same filter bar is intentional
// cell navigation, not a steal: the value input often unmounts mid-gesture and focus briefly
// lands on body, and yanking it back would leave the cursor at the end of the value instead of
// opening the cell the user pressed.
function isValueFocusSteal(
  el: EventTarget | null,
  listboxId: string,
  input: HTMLInputElement,
  lastPointerDownTarget: EventTarget | null,
): boolean {
  const container = document.getElementById(listboxId);
  if (container && el instanceof Node && container.contains(el)) {
    return true;
  }
  if (el === null || el === document.body) {
    if (lastPointerDownTarget instanceof Element) {
      const part = lastPointerDownTarget.closest<HTMLElement>('[data-part]')?.dataset.part;
      if (part === 'key' || part === 'op' || part === 'value') {
        const grid = input.closest('[role="grid"]');
        if (grid?.contains(lastPointerDownTarget)) {
          return false;
        }
      }
    }
    return !pointerLandedOutsideCombobox(lastPointerDownTarget, input, container);
  }
  return false;
}

const EditableCell = forwardRef<
  HTMLSpanElement,
  {
    onClick: () => void;
    onDeactivate: (relatedTarget?: EventTarget | null) => void;
    onInput: React.FormEventHandler<HTMLInputElement>;
    value: string;
    /** Overrides the button label without affecting the input value. */
    displayValue?: string;
    /** Overrides the input's initial value without affecting the button label or placeholder. */
    initialInputValue?: string;
    disabled?: boolean;
    isActivePrev: boolean;
    isActive: boolean;
    isActiveNext: boolean;
    /** When true, never switches to an input — the button stays rendered with a focused style when active. */
    readOnly?: boolean;
    showQuotes?: boolean;
    placeholder?: string;
    incomplete?: boolean;
    /**
     * Static text rendered immediately before the input/button — used to surface fixed
     * delimiters (e.g. the `/` slashes wrapping a regex value) as visible context that
     * sits outside the editable text.
     */
    prefix?: string;
    /** Static text rendered immediately after the input/button — see `prefix`. */
    suffix?: string;
    /**
     * Count suffix rendered after the label in button/disabled mode (e.g. "+2 more").
     * Stays fully visible while the leading value ellipsizes.
     */
    overflowSuffix?: string;
    /**
     * Icon rendered after the label in button/disabled mode — used as a dropdown
     * indicator signaling that clicking the cell opens a list to pick from.
     * Not rendered in edit mode.
     */
    trailingIcon?: IconName;
    /**
     * Optional Launchpad Tooltip body shown on hover/focus. Mirrors the LogicParen
     * type-cell tooltip pattern; supply pre-formatted ReactNode (e.g. a bold first
     * line followed by a description) for richer content.
     */
    tooltip?: React.ReactNode;
    /**
     * When true, the editable input only accepts characters that can form a valid
     * number — digits, a single decimal point, and a leading sign. Non-numeric
     * keystrokes (and pasted non-numeric text) are dropped via `onBeforeInput`.
     * Without `durationUnits`, the input also surfaces `inputMode="decimal"` for
     * mobile keyboards.
     */
    numberOnly?: boolean;
    /**
     * When true with `numberOnly`, allow observability duration suffixes
     * (`ns`, `us`, `ms`, `s`, `m`, `h`) so values like `5ms` can be typed.
     * Uses `inputMode="text"` so mobile keyboards still expose letter keys.
     */
    durationUnits?: boolean;
    'data-part'?: string;
    'aria-label'?: string;
    /** When set, the input renders as a combobox pointing at this listbox id so react-aria's Autocomplete gives the popover virtual focus (DOM focus stays in the input). */
    comboboxListboxId?: string;
    /** Whether the combobox popover is currently open (drives aria-expanded). */
    comboboxExpanded?: boolean;
    onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => void;
  }
>(
  (
    {
      onClick,
      onDeactivate,
      onInput,
      value,
      displayValue,
      initialInputValue,
      disabled,
      isActivePrev,
      isActive,
      isActiveNext,
      readOnly,
      showQuotes,
      placeholder,
      incomplete,
      prefix,
      suffix,
      overflowSuffix,
      trailingIcon,
      tooltip,
      numberOnly,
      durationUnits,
      'data-part': dataPart,
      'aria-label': ariaLabel,
      comboboxListboxId,
      comboboxExpanded,
      onInputKeyDown,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useInputWidthMirror(inputRef);

    // Records the user's most recent pointerdown target so a null/body focus loss can be
    // classified as a react-aria steal vs. a real click-away (see isValueFocusSteal).
    const lastPointerDownRef = useRef<EventTarget | null>(null);

    // Single owner of "while the value popover is open, DOM focus stays in the input". A
    // capture-phase focusin runs synchronously after react-aria moves focus, so whenever it
    // lands on one of our options (async suggestions loading) or on the body (react-aria
    // restoring the closing FreeText popover), we send it straight back — no timing race.
    // A parallel pointerdown listener lets us tell that body focus apart from the user clicking
    // a non-focusable area outside the filter bar (a dismissal), which we must NOT fight.
    useEffect(() => {
      if (!comboboxListboxId || readOnly || !isActive) {
        return undefined;
      }
      const onPointerDown = (e: PointerEvent) => {
        lastPointerDownRef.current = e.target;
      };
      const onFocusIn = (e: FocusEvent) => {
        const input = inputRef.current;
        if (!input || !input.isConnected || e.target === input) {
          return;
        }
        if (isValueFocusSteal(e.target, comboboxListboxId, input, lastPointerDownRef.current)) {
          input.focus();
        }
      };
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('focusin', onFocusIn, true);
      return () => {
        document.removeEventListener('pointerdown', onPointerDown, true);
        document.removeEventListener('focusin', onFocusIn, true);
        // Drop the last press so a later react-aria body restore cannot look
        // like sibling-cell navigation after this value cell has deactivated.
        lastPointerDownRef.current = null;
      };
    }, [comboboxListboxId, readOnly, isActive]);

    // Affixes only render in button/disabled mode — they're appended inline to the
    // visible label so they look like part of the value text. In edit mode the input
    // is shown alone so the user types just the inner content (e.g. the regex pattern
    // without its `/` delimiters).
    const isInputMode = !readOnly && isActive;
    const buttonLabelText = displayValue ?? (showQuotes && value === '' ? '""' : value);
    const labelWithAffixes = buttonLabelText ? `${prefix ?? ''}${buttonLabelText}${suffix ?? ''}` : '';
    // Native title keeps the full label available when CSS truncates the visible text.
    // Launchpad Tooltip (when supplied) still covers the action/name case separately.
    // Leading space keeps accessible/text content as "value +N more" when the
    // suffix is a sibling span (CSS margin alone does not insert a text space).
    const truncatedLabelTitle = overflowSuffix
      ? `${labelWithAffixes} ${overflowSuffix}`
      : labelWithAffixes || undefined;
    const cellLabel = (
      <>
        <span className={styles.cellLabel} title={truncatedLabelTitle}>
          {labelWithAffixes || (placeholder ? <span data-placeholder="">{placeholder}</span> : null)}
        </span>
        {overflowSuffix ? <span className={styles.cellLabelOverflow}>{` ${overflowSuffix}`}</span> : null}
        {trailingIcon && <Icon name={trailingIcon} size="small" className={styles.trailingIcon} aria-hidden="true" />}
      </>
    );

    const cellElement = isInputMode ? (
      <Input
        ref={inputRef}
        data-part={dataPart}
        aria-label={ariaLabel}
        title={truncatedLabelTitle}
        placeholder={placeholder || undefined}
        className={incomplete ? styles.cellIncomplete : undefined}
        data-incomplete={incomplete ? 'true' : undefined}
        {...(comboboxListboxId
          ? {
              // Declare the value input as a combobox so react-aria's Autocomplete keeps DOM
              // focus here and gives the option list virtual focus (the async suggestions can't steal it).
              role: 'combobox' as const,
              'aria-expanded': comboboxExpanded,
              'aria-controls': comboboxListboxId,
              'aria-autocomplete': 'list' as const,
            }
          : {})}
        {...(numberOnly ? { inputMode: (durationUnits ? 'text' : 'decimal') as 'text' | 'decimal' } : {})}
        onBeforeInput={
          numberOnly
            ? (e: React.FormEvent<HTMLInputElement> & { data?: string | null; nativeEvent: InputEvent }) => {
                // Only allow digits, a single decimal point, and a leading sign.
                // `e.nativeEvent.data` is the inserted text (null for deletions, which
                // we never block). For paste / drag-and-drop the whole pasted string
                // arrives at once so we validate the entire chunk, not character-by-
                // character. The full-value regex on the projected new value catches
                // cases like a second `.` or a `-` that isn't at the start.
                const inserted = e.nativeEvent.data ?? null;
                if (inserted === null) {
                  return;
                }
                const input = e.currentTarget;
                const start = input.selectionStart ?? input.value.length;
                const end = input.selectionEnd ?? input.value.length;
                const next = `${input.value.slice(0, start)}${inserted}${input.value.slice(end)}`;
                if (!durationUnits && !/^-?\d*\.?\d*$/.test(next)) {
                  e.preventDefault();
                }
                if (durationUnits && !isDurationValueInput(next)) {
                  e.preventDefault();
                }
              }
            : undefined
        }
        onInput={onInput}
        onBlur={(e) => {
          // A blur into our own option or a programmatic focus move to nowhere is react-aria
          // stealing focus, not the user leaving — keep the cell open; the focusin owner above
          // restores focus to the input. But a null/body blur that the user provoked by clicking
          // a non-focusable area outside the filter bar is a real dismissal, so we deactivate.
          if (
            comboboxListboxId &&
            isValueFocusSteal(e.relatedTarget, comboboxListboxId, e.currentTarget, lastPointerDownRef.current)
          ) {
            return;
          }
          onDeactivate(e.relatedTarget);
        }}
        defaultValue={initialInputValue ?? value}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (comboboxExpanded && e.currentTarget.closest('[data-autocomplete-active="true"]')) {
              return;
            }
            e.preventDefault();
            const container = inputRef.current?.parentElement;
            onDeactivate();
            requestAnimationFrame(() => {
              container?.querySelector<HTMLElement>('button')?.focus();
            });
            return;
          }
          // Escape: mirror the operator cell's behavior. The container-level handler in
          // useAutocompleteNavigation can race with the popover's own focus restoration
          // (the trigger ref points at an aria-hidden span, so restoration falls back to
          // body), leaving the cell button unfocused. Handling Escape locally here lets us
          // synchronously stop propagation and refocus the cell's button via RAF after the
          // input re-renders back into a button.
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            const container = inputRef.current?.parentElement;
            onDeactivate();
            requestAnimationFrame(() => {
              container?.querySelector<HTMLElement>('button')?.focus();
            });
            return;
          }
          onInputKeyDown?.(e);
        }}
      />
    ) : disabled ? (
      <span
        aria-label={ariaLabel}
        title={truncatedLabelTitle}
        className={clsx(styles.cell, styles.cellDisabled)}
        data-part={dataPart}
      >
        {cellLabel}
      </span>
    ) : (
      <Button
        aria-label={ariaLabel}
        className={clsx(styles.cell, incomplete && styles.cellIncomplete, {
          [styles.cellFocusedPrev]: isActivePrev,
          [styles.cellFocused]: isActive,
          [styles.cellFocusedNext]: isActiveNext,
        })}
        data-part={dataPart}
        data-empty={labelWithAffixes ? undefined : 'true'}
        data-incomplete={incomplete ? 'true' : undefined}
        onBlur={isActive ? (e) => onDeactivate(e.relatedTarget) : undefined}
        onClick={() => {
          if (!disabled) {
            onClick();
          }
        }}
        onKeyDown={(e) => {
          onInputKeyDown?.(e);
        }}
      >
        <span className={styles.cellContent}>{cellLabel}</span>
      </Button>
    );

    return (
      <span className={styles.editableCell}>
        <span ref={ref} aria-hidden="true" className={styles.editableCellOverlay} />
        {tooltip ? (
          <TooltipTrigger delay={500} isDisabled={isInputMode}>
            {cellElement}
            <Tooltip>{tooltip}</Tooltip>
          </TooltipTrigger>
        ) : (
          cellElement
        )}
      </span>
    );
  },
);

EditableCell.displayName = 'EditableCell';

// Re-export from types.ts for backward compatibility.
export type { FieldOptions };

interface LozengeFilterProps {
  /** Stable identifier used to track which lozenge has the open popover. */
  tokenKey: string;
  value: FilterToken;
  onChange: (value: FilterToken) => void;
  onRemove: () => void;
  removable?: boolean;
  icon?: IconName;
  /** Sample values per field, used to populate autocomplete for the value cell. */
  options?: FieldOptions[];
  /** When provided, key/op/value selection dispatches structured actions to the QueryBuilder context. */
  parsedToken?: Extract<ParsedToken, { type: 'FILTER' }>;
  variant?: 'default' | 'inline';
  onEditValues?: (values: Array<string | number | boolean>) => void;
  truncateSingleValue?: boolean;
}

export function LozengeFilter({
  tokenKey,
  value,
  onChange,
  onRemove,
  removable,
  icon,
  options,
  parsedToken,
  variant = 'default',
  onEditValues,
  truncateSingleValue = true,
}: LozengeFilterProps) {
  const {
    activePopover,
    activePopoverRef,
    setActivePopover,
    dispatch,
    wrapperRef,
    parsedQuery,
    setActiveValueSearch,
    query,
    suppressNextPopoverCloseCommit,
  } = useQueryBuilderContext();
  const {
    readonly,
    gridState,
    showTypeLabels,
    allowExistsOption,
    relationalFieldOptions,
    onLoadValues,
    autocompleteFooter,
    autocompleteDetails,
  } = useFilterBarContext();
  const filterDisplayValue =
    'values' in value ? (value.values?.join(',') ?? '') : 'value' in value ? String(value.value ?? '') : '';
  const isUnaryExists = filterDisplayValue === PRESENT_VALUE_SENTINEL;
  const { containerProps, cellProps } = useQueryBuilderGridItem(
    tokenKey,
    gridState,
    `${value.field}:${value.operator}:${filterDisplayValue}`,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const keyRef = useRef<HTMLSpanElement>(null);
  const opRef = useRef<HTMLSpanElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);

  const inlineValueTriggerRef = useRef<HTMLButtonElement>(null);
  const [inlineValueOpen, setInlineValueOpen] = useState(false);
  const inlineValueLastCloseAtRef = useRef(0);

  // Values that have appeared in the multi-value input during the current edit session.
  // Seeded when the value cell opens; accumulated as options are selected.
  // Persists even when values are deselected so options remain visible in the autocomplete.
  const [multiSessionOptions, setMultiSessionOptions] = useState<string[]>([]);

  // Tracks the segment of the value input that the cursor is currently within, for
  // cursor-aware autocomplete filtering. Null until the user first interacts with the input.
  const [cursorSegment, setCursorSegment] = useState<string | null>(null);
  // Where that segment sits in the committed values array, so a suggestion picked from the
  // list can replace the segment being typed instead of landing beside it. -1 = no segment.
  const [cursorSegmentIndex, setCursorSegmentIndex] = useState(-1);
  // Which committed-value index the user last typed in. Distinct from `cursorSegmentIndex`
  // (where the caret is now): after a keystroke, moving into another value and picking a
  // suggestion must add beside it, not drop that other value. Reset on popover open.
  const [editedSegmentIndex, setEditedSegmentIndex] = useState(-1);
  // Whether the user has actually edited the value text since opening the popover. Used to
  // decide when to *filter* the value suggestions (vs. only sort/highlight). Reset on open so
  // that merely focusing, clicking, or arrow-keying through a committed value doesn't collapse
  // the list to the committed substring.
  const [hasEditedValue, setHasEditedValue] = useState(false);
  // Same idea for the key cell: `value.field` doubles as the cell's live text, so on a fresh
  // open it holds the committed field name and filtering by it would collapse the list to the
  // current field. Only filter once the user has actually retyped the key.
  const [hasEditedKey, setHasEditedKey] = useState(false);
  const [opDraft, setOpDraft] = useState('');
  const [hasEditedOp, setHasEditedOp] = useState(false);
  const [operatorUnset, setOperatorUnset] = useState(false);
  const [lozengeFocused, setLozengeFocused] = useState(false);
  const skipOpDraftResetRef = useRef(false);
  const [boolDraft, setBoolDraft] = useState('');
  const [hasEditedBool, setHasEditedBool] = useState(false);
  const [valueCleared, setValueCleared] = useState(false);

  const [loadedValues, setLoadedValues] = useState<Record<string, readonly FieldSampleValue[]>>({});
  // Only permanently skip fields that returned values; empty/cancel/fail stay retryable.
  const loadedFieldsRef = useRef<Set<string>>(new Set());
  const inFlightFieldsRef = useRef<Set<string>>(new Set());
  // A sibling-cell blur updates activePopover before the ensuing click runs.
  const pointerDownPartRef = useRef<'key' | 'op' | 'value' | null>(null);
  const skipSamePartToggleRef = useRef<'key' | 'op' | 'value' | null>(null);
  // Stays set for the whole cell pointer gesture (pointerdown → pointerup), including after
  // blur clears pointerDownPartRef. React Aria's non-modal popover treats a sibling cell as
  // an outside interaction and would otherwise call onOpenChange(false) on pointerup.
  const cellPointerGestureRef = useRef<'key' | 'op' | 'value' | null>(null);

  // Resolve field metadata within this lozenge's scope first (so a same-named field
  // in another scope can't supply the wrong type), then fall back to any scope.
  const baseFieldData = useMemo(() => {
    const all = options ?? [];
    const scope = scopeForExpressionType(
      parsedToken ? expressionTypeAtOffset(parsedQuery, parsedToken.location.start.offset) : undefined,
      relationalFieldOptions,
    );
    return (
      fieldOptionsForScope(all, scope).find((o) => o.field === value.field) ?? all.find((o) => o.field === value.field)
    );
  }, [options, value.field, parsedToken, parsedQuery, relationalFieldOptions]);

  const fieldData = useMemo(() => {
    if (!baseFieldData) {
      return baseFieldData;
    }
    const loaded = loadedValues[baseFieldData.field];
    if (!loaded || loaded.length === 0) {
      return baseFieldData;
    }
    const seen = new Set(baseFieldData.sampleValues.map((s) => String(s.value)));
    const extra = loaded
      .filter((s) => !seen.has(String(s.value)))
      .map((s) => ({ value: s.value, approxCount: s.approxCount ?? 0, displayValue: s.displayValue }));
    return extra.length > 0
      ? { ...baseFieldData, sampleValues: [...baseFieldData.sampleValues, ...extra] }
      : baseFieldData;
  }, [baseFieldData, loadedValues]);

  // The field-edit dropdown must offer only fields valid in this lozenge's scope (e.g. session
  // fields inside any_session), matching fieldData above and the FreeTextToken creation flow.
  const scopedOptions = useMemo(() => {
    const scope = scopeForExpressionType(
      parsedToken ? expressionTypeAtOffset(parsedQuery, parsedToken.location.start.offset) : undefined,
      relationalFieldOptions,
    );
    return fieldOptionsForScope(options ?? [], scope);
  }, [options, parsedToken, parsedQuery, relationalFieldOptions]);

  // Guard by readonly: activePopover lives in QueryBuilderContext and is shared
  // across every FilterBar in the tree. Without this gate, a readonly lozenge
  // would render its own popover whenever an editable lozenge with the same
  // tokenKey opens one — visible whenever multiple FilterBars render the same
  // parsedQuery (e.g. an editable + readonly pair).
  const isThisActive =
    !readonly &&
    activePopover?.tokenKey === tokenKey &&
    activePopover.part !== 'operator' &&
    !(isUnaryExists && activePopover.part === 'value');
  const activePart = isThisActive && activePopover.part !== 'operator' ? activePopover.part : null;
  const focusInsideLozenge = lozengeFocused || isThisActive;

  // Clear the cell pointer gesture after pointerup/cancel so a later genuine
  // outside dismiss can close the popover. Deferred a frame so React Aria's
  // interact-outside handler — also bound to pointerup — still sees the flag,
  // and so the finishing click (which fires in the same turn as pointerup) can
  // still consume skipSamePartToggle. Do not listen for lostpointercapture:
  // React Aria Buttons capture the pointer, so swapping a cell button for an
  // input unmounts the capture target while the pointer is still down and would
  // clear the flag one frame too early.
  useEffect(() => {
    const endGesture = () => {
      requestAnimationFrame(() => {
        cellPointerGestureRef.current = null;
        skipSamePartToggleRef.current = null;
      });
    };
    document.addEventListener('pointerup', endGesture, true);
    document.addEventListener('pointercancel', endGesture, true);
    return () => {
      document.removeEventListener('pointerup', endGesture, true);
      document.removeEventListener('pointercancel', endGesture, true);
    };
  }, []);

  const loadField = baseFieldData?.field;
  const loadHasStaticSamples = (baseFieldData?.sampleValues.length ?? 0) > 0;
  const loadValueType = baseFieldData?.valueType;
  const loadAllowCustom = !!baseFieldData?.allowCustom;
  useEffect(() => {
    if (readonly || !onLoadValues || activePart !== 'value' || !loadField || isUnaryExists) {
      return;
    }
    if (
      loadHasStaticSamples ||
      loadValueType === 'boolean' ||
      loadValueType === 'number' ||
      !loadAllowCustom ||
      loadedFieldsRef.current.has(loadField) ||
      inFlightFieldsRef.current.has(loadField)
    ) {
      return;
    }
    const field = loadField;
    const inFlight = inFlightFieldsRef.current;
    inFlight.add(field);
    let cancelled = false;
    void Promise.resolve(onLoadValues(field))
      .then((values) => {
        if (cancelled) {
          return;
        }
        if (values.length > 0) {
          loadedFieldsRef.current.add(field);
          setLoadedValues((prev) => ({ ...prev, [field]: values }));
        }
      })
      .catch(() => {})
      .finally(() => {
        inFlight.delete(field);
      });
    return () => {
      cancelled = true;
      inFlight.delete(field);
    };
  }, [
    readonly,
    onLoadValues,
    activePart,
    loadField,
    loadHasStaticSamples,
    loadValueType,
    loadAllowCustom,
    isUnaryExists,
  ]);

  // Regex operators (// and !//) take a single regex literal, never a multi-value list: a regex
  // already expresses "any of" via alternation (a|b), its body may legally contain the comma the
  // multiselect path splits on (e.g. /\d{1,3}/), and the backend forwards it as one value. So they
  // opt out of multiselect and render a single-literal preview popover (see the popover layout below).
  const isRegexValueOp = value.operator === '//' || value.operator === '!//';
  // Boolean fields only ever take a single value (true/false) — the one hard "never multiselect"
  // gate. Detected from the explicit `valueType`, falling back to inferring from sample values.
  let isBooleanField = false;
  if (fieldData?.valueType) {
    isBooleanField = fieldData.valueType === 'boolean';
  } else if (fieldData?.sampleValues && fieldData.sampleValues.length > 0) {
    isBooleanField = inferFieldType(fieldData.sampleValues) === 'BOOLEAN';
  }
  // Number fields restrict the input to digits / decimal point / leading sign, and the popover's
  // "Search for value:" pin reads "Search for number:" instead. Detection is purely from the
  // explicit `valueType` (inferFieldType doesn't return NUMBER).
  const isNumberField = fieldData?.valueType === 'number' || fieldData?.valueType === 'duration';
  const isDurationField = fieldData?.valueType === 'duration';
  // Multiselect is FIELD-driven; `fieldAllowsMultipleValues` is the shared definition of when a
  // comma separates values, so the value cell and the paths that build a filter from typed text
  // (FreeTextToken's inline promotion) can't disagree about it.
  const fieldAllowsMultiselect = fieldAllowsMultipleValues(fieldData, value.operator);
  // Whether the field can hold multiple values at once (multi-valued/list attribute). When true,
  // chosen values combine with AND; falsy (the default) means OR. No field sets this yet, so AND is
  // dormant and OR is the live path — see the value-commit branch in handleSelect.
  const fieldHoldsMultiple = !!fieldData?.supportsMultipleValues;
  const isMultiSelect = activePart === 'value' && fieldAllowsMultiselect;

  const valueSampleValues = useMemo(() => {
    let samples = fieldData?.sampleValues ?? [];
    const isBoolean =
      fieldData?.valueType === 'boolean' || (samples.length > 0 && inferFieldType(samples) === 'BOOLEAN');
    if (isBoolean) {
      samples = ensureBooleanSampleValues(
        samples.length > 0 ? samples : [{ value: 'true' }, { value: 'false' }],
        (booleanValue) => ({
          value: booleanValue,
        }),
      );
    }
    return fieldAllowsMultiselect ? samples.filter((s) => !String(s.value).includes(',')) : samples;
  }, [fieldData?.sampleValues, fieldData?.valueType, fieldAllowsMultiselect]);

  const spanRefs = { key: keyRef, op: opRef, value: valueRef } as const;
  const activeTriggerRef = (
    activePart && activePart !== 'type' ? spanRefs[activePart] : keyRef
  ) as React.RefObject<HTMLElement | null>;

  function handleCellPress(part: 'key' | 'op' | 'value') {
    if (readonly) {
      return;
    }
    // Switching value→op (or any sibling) unmounts the value <input>, which can shift
    // layout under a still-down pointer. The ensuing click then retargets onto a neighbor
    // cell — ignore that retarget so the part that received pointerdown keeps ownership.
    const gesturedPart = cellPointerGestureRef.current;
    if (gesturedPart && gesturedPart !== part) {
      return;
    }
    const skipToggle = skipSamePartToggleRef.current === part;
    skipSamePartToggleRef.current = null;
    if (!skipToggle && isThisActive && activePopover?.part === part) {
      setActivePopover(null);
    } else {
      if (part === 'key') {
        setHasEditedKey(false);
      }
      if (part === 'op') {
        setHasEditedOp(false);
      }
      // Value-popover session state (cursor segment, hasEditedValue, multiSessionOptions)
      // is seeded by the open-transition effect below, which covers every open path.
      setActivePopover({ tokenKey, part });
    }
  }

  function handleCellDeactivate(relatedTarget?: EventTarget | null, fromPart?: 'key' | 'op' | 'value') {
    const live = activePopoverRef.current;
    if (fromPart && live?.tokenKey === tokenKey && live.part !== fromPart && live.part !== 'operator') {
      return;
    }
    if (relatedTarget instanceof Element && containerRef.current?.contains(relatedTarget)) {
      const part = relatedTarget.closest<HTMLElement>('[data-part]')?.dataset.part;
      if (part === 'key' || part === 'op' || part === 'value') {
        // Only pointer transitions have a click still to come. Keyboard grid
        // navigation must leave the next same-cell click free to toggle closed.
        if (pointerDownPartRef.current === part) {
          skipSamePartToggleRef.current = part;
          window.setTimeout(() => {
            if (skipSamePartToggleRef.current === part) {
              skipSamePartToggleRef.current = null;
            }
          }, 0);
        }
        pointerDownPartRef.current = null;
        setActivePopover({ tokenKey, part });
        return;
      }
    }
    // React Aria Buttons often preventDefault on pointerdown, so focus never moves to
    // the pressed sibling before our pointerdown setState unmounts the value <input>.
    // That unmount blurs with a null relatedTarget — keep the cell the gesture committed.
    if (cellPointerGestureRef.current) {
      const part = cellPointerGestureRef.current;
      pointerDownPartRef.current = null;
      setActivePopover({ tokenKey, part });
      return;
    }
    pointerDownPartRef.current = null;
    skipSamePartToggleRef.current = null;
    setActivePopover(null);
  }

  function handleCellInput(part: 'key' | 'op' | 'value', text: string) {
    if (parsedToken) {
      if (part === 'key') {
        if (!isValidKey(text)) {
          return;
        }
        setHasEditedKey(true);
        dispatch({ type: 'UPDATE_FILTER_KEY', token: parsedToken, key: text });
      } else if (part === 'op') {
        setHasEditedOp(true);
        setOpDraft(text);
      } else if (part === 'value') {
        dispatch({ type: 'UPDATE_FILTER_VALUE', token: parsedToken, value: text });
      }
    } else {
      if (part === 'key') {
        if (!isValidKey(text)) {
          return;
        }
        setHasEditedKey(true);
        const normalizedKey = text;
        if ('values' in value) {
          onChange({ field: normalizedKey, operator: value.operator, values: value.values });
        } else {
          onChange({
            field: normalizedKey,
            operator: value.operator,
            value: 'value' in value ? value.value : undefined,
          });
        }
      } else if (part === 'op') {
        setHasEditedOp(true);
        setOpDraft(text);
      } else if (part === 'value') {
        if ('values' in value) {
          onChange({ field: value.field, operator: value.operator, values: [text] });
        } else {
          onChange({ field: value.field, operator: value.operator, value: text });
        }
      }
    }
  }

  useLayoutEffect(() => {
    if (activePart !== 'key' && activePart !== 'op' && activePart !== 'value') {
      return undefined;
    }
    const findEl = () => containerRef.current?.querySelector<HTMLElement>(`[data-part="${activePart}"]`);
    findEl()?.focus();
    const id = requestAnimationFrame(() => findEl()?.focus());
    return () => cancelAnimationFrame(id);
  }, [activePart]);

  // Track cursor position in the value input so autocomplete can filter by the
  // segment the cursor is currently within (not just the last segment).
  useEffect(() => {
    if (activePart !== 'value') {
      return;
    }
    const input = containerRef.current?.querySelector<HTMLInputElement>('[data-part="value"]');
    if (!input) {
      return;
    }

    const el = input;
    function updateFromCursor() {
      const pos = el.selectionStart ?? el.value.length;
      setCursorSegment(getSegmentAtCursor(el.value, pos));
      setCursorSegmentIndex(getSegmentIndexAtCursor(el.value, pos));
    }

    // keyup: catches arrow-key cursor movement (no text change, so onInput won't fire)
    // click: catches mouse cursor placement
    el.addEventListener('keyup', updateFromCursor);
    el.addEventListener('click', updateFromCursor);
    return () => {
      el.removeEventListener('keyup', updateFromCursor);
      el.removeEventListener('click', updateFromCursor);
    };
  }, [activePart]);

  /**
   * Commits the cursor segment as a value when the user picks the pinned
   * "Search for value:" option. For single-select the typed text is already
   * mirrored to state by onInput, so we only need to close the popover. For
   * multi-select the segment must be added to the values list and to
   * multiSessionOptions so it persists as a popover option after deselection.
   */
  // Commit a value (scalar or array) to the token: dispatch to the reducer in the parsedToken
  // path, else call onChange for standalone usage. Arrays flow through unchanged so stringifyToken
  // emits the unquoted comma form (field:=:a,b) that re-parses back into an array.
  function commitFilterValue(newValue: string | Array<string | number | boolean>) {
    if (parsedToken) {
      dispatch({ type: 'UPDATE_FILTER_VALUE', token: parsedToken, value: newValue });
    } else {
      onChange({ field: value.field, operator: value.operator, value: newValue });
    }
  }

  /**
   * The committed values minus the one the user is part-way through typing.
   *
   * A multiselect value cell commits its whole input on every keystroke, so a half-typed
   * word ("Produc") is already a value by the time the user clicks a suggestion. Picking
   * from the list is a completion of that word, not a second filter value, so the segment
   * under the cursor is dropped before the selection is applied. Only text the user
   * actually typed counts: clicking or arrowing into an existing value must leave it alone,
   * and the index/text pair must still match the committed values, which lag a render
   * behind the input during fast typing.
   */
  function valuesWithoutTypedSegment(): string[] {
    if (
      !hasEditedValue ||
      editedSegmentIndex < 0 ||
      editedSegmentIndex !== cursorSegmentIndex ||
      !cursorSegment ||
      cursorSegmentIndex < 0
    ) {
      return concreteSelectedValues;
    }
    if (concreteSelectedValues[cursorSegmentIndex] !== cursorSegment) {
      return concreteSelectedValues;
    }
    return concreteSelectedValues.filter((_, index) => index !== cursorSegmentIndex);
  }

  function focusAfterValueLozenge() {
    const nextGapKey = nextFreeTextFocusKey();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const input = wrapperRef.current?.querySelector<HTMLInputElement>(
          `input[data-key="${CSS.escape(nextGapKey)}"]`,
        );
        if (input) {
          input.focus();
          input.setSelectionRange(0, 0);
          return;
        }
        focusNextFreeText();
      });
    });
  }

  function commitSearchValue() {
    if (!isMultiSelect) {
      setActivePopover(null);
      focusAfterValueLozenge();
      return;
    }
    const segment = valueSearchText;
    if (!segment) {
      return;
    }
    const currentValues = concreteSelectedValues;
    const newValues = currentValues.includes(segment) ? currentValues : [...currentValues, segment];
    setMultiSessionOptions((prev) => (prev.includes(segment) ? prev : [...prev, segment]));
    commitFilterValue(newValues);
    syncValueInput(newValues);
  }

  /**
   * Sync the uncontrolled value input to `newValues` and move cursor to end.
   * A trailing ", " is appended in the DOM only — onInput's parseMultiValue
   * strips it back out before storing, so the trailing separator never reaches
   * the query state but lets the user immediately type the next value.
   */
  function syncValueInput(newValues: string[]) {
    // Match only an <input> — a `multi`, non-`allowCustom` field (e.g. a read-only multiselect)
    // renders its value cell as a <button>, which has no value/setSelectionRange to sync.
    const input = containerRef.current?.querySelector<HTMLInputElement>('input[data-part="value"]');
    if (!input) {
      return;
    }
    const formatted = newValues.length > 0 ? `${newValues.join(', ')}, ` : '';
    input.value = formatted;
    input.setSelectionRange(formatted.length, formatted.length);
    // The rewrite leaves the caret in the empty trailing segment. Setting the value
    // programmatically fires no input event, so mirror that here or the popover would keep
    // filtering by the text the selection just consumed.
    setCursorSegment('');
    setCursorSegmentIndex(-1);
    // A suggestion pick consumed the typed segment; clear editedSegmentIndex too or a later
    // click back into that value still matches valuesWithoutTypedSegment and toggling off
    // re-adds instead of removing.
    setEditedSegmentIndex(-1);
    // Programmatic value/selection updates don't reliably scroll an input's view to follow
    // the caret — even when the input is focused. After the next paint (so the mirror-driven
    // width adjustment in useInputWidthMirror has run), pin the horizontal scroll to the
    // right edge so the just-moved caret is visible without needing a keystroke to nudge it.
    requestAnimationFrame(() => {
      input.scrollLeft = input.scrollWidth;
    });
  }

  function handleSelect(selected: string | number | boolean) {
    if (activePart === 'value' && selected === SEARCH_VALUE_SENTINEL) {
      commitSearchValue();
      return;
    }

    // "exists" / "does not exist" live on the operator picker, not the value list.
    if (activePart === 'op' && typeof selected === 'string' && isExistsOperatorOption(selected)) {
      const newOp: Operator = selected === NOT_EXISTS_OPERATOR_OPTION ? '!=' : '=';
      setHasEditedOp(false);
      setOperatorUnset(false);
      if (parsedToken) {
        dispatch({ type: 'UPDATE_FILTER_OP', token: parsedToken, op: newOp, value: PRESENT_VALUE_SENTINEL });
      } else if ('values' in value) {
        onChange({ field: value.field, operator: newOp, values: [PRESENT_VALUE_SENTINEL] });
      } else {
        onChange({ field: value.field, operator: newOp, value: PRESENT_VALUE_SENTINEL });
      }
      setActivePopover(null);
      requestAnimationFrame(() => {
        containerRef.current?.querySelector<HTMLElement>('button[data-part="op"]')?.focus();
      });
      return;
    }

    if (parsedToken) {
      // Dispatch structured actions so the reducer updates the raw query string
      if (activePart === 'op') {
        const newOp = selected as Operator;
        setHasEditedOp(false);
        setOperatorUnset(false);
        // Leaving exists always clears the sentinel, including for `=` / `!=`,
        // so "is" / "is not" open the value cell instead of staying unary.
        const leavingExists = displayValue === PRESENT_VALUE_SENTINEL;
        dispatch({
          type: 'UPDATE_FILTER_OP',
          token: parsedToken,
          op: newOp,
          ...(leavingExists ? { value: '' } : {}),
        });
        setActivePopover({ tokenKey, part: 'value' });
        return;
      } else if (activePart === 'value') {
        if (isMultiSelect) {
          const currentValues = valuesWithoutTypedSegment();
          const selectedStr = String(selected);
          const newValues = currentValues.includes(selectedStr)
            ? currentValues.filter((v) => v !== selectedStr)
            : [...currentValues, selectedStr];
          setMultiSessionOptions((prev) => (prev.includes(selectedStr) ? prev : [...prev, selectedStr]));
          // Live (OR) path: dispatch the ARRAY so stringifyToken emits field:=:a,b, which the
          // converters treat as OR. When fieldHoldsMultiple the values would instead be AND-joined
          // as separate tokens (which the converters already AND) — dormant until a field opts in.
          commitFilterValue(newValues);
          syncValueInput(newValues);
          return; // keep popover open
        }
        setHasEditedBool(false);
        setBoolDraft(String(selected));
        setValueCleared(false);
        dispatch({
          type: 'UPDATE_FILTER_VALUE',
          token: parsedToken,
          value: String(selected),
          focusOverride: { itemKey: nextFreeTextFocusKey(), caret: 'start' },
        });
        focusAfterValueLozenge();
      } else if (activePart === 'key') {
        const newField = String(selected);
        // Resolve from the same scoped list the key picker offered (scopedOptions), not the
        // merged full list — a same-named field in another scope would supply the wrong type.
        const newFieldOptions = scopedOptions.find((o) => o.field === newField);
        let newOp: Operator | undefined;
        if (newFieldOptions) {
          // Prefer an explicit string valueType over sample inference so boolean-like
          // sample values on a string field don't flip the type. Only reset the
          // operator when it is incompatible with the new field's type — preserve
          // compatible ops (e.g. `contains` when switching between string fields).
          const fieldType =
            newFieldOptions.valueType === 'string' ? 'STRING' : inferFieldType(newFieldOptions.sampleValues);
          const operatorAllowed =
            !newFieldOptions.allowedOperators || newFieldOptions.allowedOperators.includes(value.operator);
          if (!isOperatorCompatible(value.operator, fieldType) || !operatorAllowed) {
            newOp = newFieldOptions.allowedOperators?.[0] ?? getDefaultOperator(fieldType);
          }
        }
        dispatch({ type: 'UPDATE_FILTER_KEY', token: parsedToken, key: newField, newOp });
      }
    } else {
      // Fallback: call onChange with the updated FilterToken (for standalone / hardcoded usage)
      if (activePart === 'op') {
        const newOp = selected as Operator;
        setHasEditedOp(false);
        setOperatorUnset(false);
        const leavingExists = displayValue === PRESENT_VALUE_SENTINEL;
        const v = leavingExists
          ? ''
          : 'values' in value
            ? value.values?.join(',')
            : 'value' in value
              ? value.value
              : undefined;
        onChange({ field: value.field, operator: newOp, value: v });
        setActivePopover({ tokenKey, part: 'value' });
        return;
      } else if (activePart === 'value') {
        if (isMultiSelect) {
          const currentValues = valuesWithoutTypedSegment();
          const selectedStr = String(selected);
          const newValues = currentValues.includes(selectedStr)
            ? currentValues.filter((v) => v !== selectedStr)
            : [...currentValues, selectedStr];
          setMultiSessionOptions((prev) => (prev.includes(selectedStr) ? prev : [...prev, selectedStr]));
          onChange({ field: value.field, operator: value.operator, value: newValues });
          syncValueInput(newValues);
          return;
        }
        setHasEditedBool(false);
        setBoolDraft(String(selected));
        setValueCleared(false);
        if ('values' in value) {
          onChange({ field: value.field, operator: value.operator, values: [String(selected)] });
        } else {
          onChange({ field: value.field, operator: value.operator, value: selected });
        }
      } else if (activePart === 'key') {
        if ('values' in value) {
          onChange({ field: String(selected), operator: value.operator, values: value.values });
        } else if ('value' in value) {
          onChange({ field: String(selected), operator: value.operator, value: value.value });
        }
      }
    }
    setActivePopover(null);
  }

  const rawDisplayValue =
    'values' in value
      ? (value.values?.join(',') ?? '')
      : Array.isArray(value.value)
        ? value.value.map(String).join(',')
        : 'value' in value
          ? String(value.value ?? '')
          : '';
  const displayValue = rawDisplayValue === '""' ? '' : rawDisplayValue;
  if (valueCleared && displayValue !== '') {
    setValueCleared(false);
  }
  const showClearedBoolean = isBooleanField && valueCleared && displayValue === '';

  // The committed values as a structured array — the source of truth for selection state
  // (checkmarks, toggles, labels, counts). Read straight off the token so a value containing a comma
  // isn't split into phantom selections the way re-parsing the comma-joined displayValue would.
  const committedValues = useMemo<string[]>(() => {
    const raw =
      'values' in value
        ? (value.values ?? [])
        : Array.isArray(value.value)
          ? value.value
          : 'value' in value && value.value !== null && value.value !== undefined
            ? [value.value]
            : [];
    return raw.map(String).filter((v) => v !== '');
  }, [value]);

  // Number of committed values, used to pick the "… one of" operator-label suffix. A regex op is
  // always one literal (its body may contain commas), so force 1; otherwise count committedValues.
  const committedValueCount = isRegexValueOp ? 1 : committedValues.length;

  // Seed the value-popover session state whenever the value popover transitions to open,
  // regardless of which path opened it: a cell press, an operator change hopping focus to
  // the value cell, a key selection auto-opening it, or an external open from FreeTextToken.
  // Without this, a prior value-typing session leaves `hasEditedValue` set and a stale
  // `cursorSegment`, so filtering kicks in immediately and hides most sample values in the
  // newly opened list. useLayoutEffect so the re-seeded state flushes before paint.
  const wasValuePopoverOpen = useRef(false);
  useLayoutEffect(() => {
    const isOpen = activePart === 'value';
    if (isOpen && !wasValuePopoverOpen.current) {
      // Seed cursor segment to '' for present mode (sentinel) and for multiselect fields with
      // existing values so valueSearchText starts empty (show all options).
      setCursorSegment(displayValue === PRESENT_VALUE_SENTINEL || (fieldAllowsMultiselect && displayValue) ? '' : null);
      setCursorSegmentIndex(-1);
      setEditedSegmentIndex(-1);
      setHasEditedValue(false);
      if (isBooleanField) {
        setBoolDraft(displayValue === PRESENT_VALUE_SENTINEL ? '' : displayValue);
        setHasEditedBool(false);
      }
      if (fieldAllowsMultiselect) {
        // Don't seed the sentinel into multiSessionOptions — it's not a real value and
        // would create a duplicate option with no displayName in the popover.
        setMultiSessionOptions(displayValue === PRESENT_VALUE_SENTINEL ? [] : committedValues);
      }
    }
    wasValuePopoverOpen.current = isOpen;
  }, [activePart, displayValue, fieldAllowsMultiselect, committedValues, isBooleanField]);

  const operatorCellDisplay = getOperatorCellDisplay(value.operator, displayValue, {
    oneOf: fieldAllowsMultiselect && !fieldHoldsMultiple && committedValueCount > 1,
  });

  const popoverOpDraft =
    activePopover?.tokenKey === tokenKey && activePopover.part === 'op' ? activePopover.draft : undefined;
  const wasOpPopoverOpen = useRef(false);
  const autoFocusFirstOp =
    activePopover?.tokenKey === tokenKey && activePopover.part === 'op' && !!activePopover.autoFocusFirst;
  useLayoutEffect(() => {
    const isOpen = activePart === 'op';
    if (isOpen && !wasOpPopoverOpen.current) {
      if (skipOpDraftResetRef.current) {
        skipOpDraftResetRef.current = false;
      } else if (popoverOpDraft !== undefined) {
        setOpDraft(popoverOpDraft);
        setHasEditedOp(true);
      } else if (autoFocusFirstOp || operatorUnset) {
        setOpDraft('');
        setHasEditedOp(true);
      } else {
        setOpDraft(operatorCellDisplay);
        setHasEditedOp(false);
      }
    } else if (isOpen && popoverOpDraft !== undefined && !hasEditedOp) {
      setOpDraft(popoverOpDraft);
      setHasEditedOp(true);
    } else if (isOpen && autoFocusFirstOp && !hasEditedOp) {
      setOpDraft('');
      setHasEditedOp(true);
    }
    wasOpPopoverOpen.current = isOpen;
  }, [activePart, operatorCellDisplay, popoverOpDraft, hasEditedOp, autoFocusFirstOp, operatorUnset]);

  // Non-custom mode: cells render as read-only buttons that only accept
  // autocomplete selections, and cell labels use the matching `displayValue`
  // from FieldOptions (key) or sampleValues (value) instead of the raw text.
  //
  // When `fieldData` is undefined the field is unknown to this FilterBar — the
  // user is either mid-typing a custom key or has just committed a brand-new
  // lozenge from FreeTextToken's "Search for field:" option with a custom
  // field name. Both cases need free-text input on the value cell; otherwise
  // the value cell mounts as a read-only button the user cannot type into.
  // Treat unknown fields as custom for both cells.
  const allowCustomKey = fieldData ? fieldData.allowCustom === true : true;
  const allowCustomValue = isBooleanField ? false : fieldData ? fieldData.allowCustom === true : true;
  const valueCellReadOnly = isBooleanField ? false : !allowCustomValue;

  // Look up a sample value's display label by its raw value. Used in non-custom
  // mode to translate the stored value into the human-readable label.
  const sampleDisplayLabel = (raw: string): string =>
    fieldData?.sampleValues?.find((s) => String(s.value) === raw)?.displayValue ?? raw;

  // For multi-select fields, the committed value cell button shows only the first
  // selection plus a count of additional ones (e.g. "USA, +2 more") rather than
  // the full comma-separated list.
  const multiValueParts = fieldAllowsMultiselect ? committedValues : null;
  const multiValueLabels =
    multiValueParts && !allowCustomValue ? multiValueParts.map(sampleDisplayLabel) : multiValueParts;
  const multiValueButtonLabel =
    multiValueLabels && multiValueLabels.length > 1 ? String(multiValueLabels[0]) : undefined;
  const multiValueOverflowSuffix =
    multiValueLabels && multiValueLabels.length > 1
      ? msg('queryBuilder.filterToken.moreCount', { count: multiValueLabels.length - 1 })
      : undefined;

  // The value the Autocomplete wrapper reports as the field's text. It must mirror the *whole*
  // value input, not just the cursor segment: a multiselect field's input holds the full
  // comma-joined list, so feeding only the active segment here would misrepresent the field value
  // and (should react-aria ever control the input) drop the other committed values on re-render.
  // Cursor-segment-aware option *filtering* is unaffected — it flows separately via valueSearchText
  // into AutocompletePopover's searchText. Present mode reports empty (the input is blank); regex
  // is excluded (its single-literal preview must not be filtered/overwritten by the typed text).
  const valueCellInputText = useMemo(() => {
    if (activePart !== 'value' || isRegexValueOp || displayValue === PRESENT_VALUE_SENTINEL) {
      return '';
    }
    if (isBooleanField) {
      return hasEditedBool ? boolDraft : displayValue;
    }
    return fieldAllowsMultiselect ? (multiValueParts?.join(', ') ?? '') : displayValue;
  }, [
    activePart,
    isRegexValueOp,
    displayValue,
    fieldAllowsMultiselect,
    multiValueParts,
    isBooleanField,
    hasEditedBool,
    boolDraft,
  ]);

  // The text used to filter autocomplete options: for the value cell, this is the
  // segment at the current cursor position rather than the last comma segment.
  const valueSearchText = useMemo(() => {
    if (activePart !== 'value') {
      return '';
    }
    if (isBooleanField) {
      return hasEditedBool ? boolDraft : '';
    }
    // If the user has interacted with the input, use the cursor-aware segment.
    if (cursorSegment !== null) {
      return cursorSegment;
    }
    // Fallback before first interaction: use the last segment (mirrors prior behavior).
    return fieldAllowsMultiselect ? (displayValue.split(',').at(-1)?.trim() ?? '') : displayValue;
  }, [activePart, cursorSegment, displayValue, fieldAllowsMultiselect, isBooleanField, hasEditedBool, boolDraft]);

  // Only *filter* the value suggestions once the user has actually edited the value text.
  // On a fresh open (or when the user merely clicks / arrow-keys through an existing value),
  // a single-value field's valueSearchText falls back to the whole committed value, and
  // filtering by it would hide every other option until the value is cleared — so until the
  // text is edited we show all options (matching the multi-value cell, which seeds an empty
  // segment on open). Editing is tracked separately from cursor movement because keyup/click
  // also set cursorSegment without changing the text.
  const isTypingValue = hasEditedValue;

  // Empty until the value is edited, so opening a cell does not fetch only the committed value.
  const reportedValueSearch = activePart === 'value' ? (isTypingValue ? valueSearchText : '') : undefined;
  useEffect(() => {
    setActiveValueSearch?.(reportedValueSearch);
  }, [setActiveValueSearch, reportedValueSearch]);

  // Derive the type-appropriate options for the active part (unfiltered — Autocomplete handles text filtering).
  const rawPopoverOptions = useMemo(() => {
    if (activePart === 'op') {
      let allowedOps = OPERATOR_OPTIONS;
      if (fieldData?.allowedOperators) {
        allowedOps = OPERATOR_OPTIONS.filter((o) => fieldData.allowedOperators?.includes(o.value));
      } else if (fieldData?.valueType === 'string') {
        allowedOps = OPERATOR_OPTIONS.filter((o) => o.value === '=' || o.value === '!=');
      }
      const withExists = withExistsOperatorOptions(allowedOps, allowExistsOption !== false);
      if (hasEditedOp && opDraft.trim()) {
        const opSearch = opDraft.trim();
        return withExists
          .filter((o) => optionMatchesSearch(o, opSearch))
          .sort((a, b) => compareOptionMatch(a, b, opSearch));
      }
      return withExists;
    }

    if (activePart === 'value') {
      // Regex operators (// and !//) accept a single regex literal — short-circuit the usual
      // sample/present/search options and surface only one selectable entry that previews
      // the regex about to be committed. Below it, render every sample value as a non-
      // selectable preview row so the user can see which existing values match the pattern
      // they're typing — matched rows get a green check via `trailingIconName`.
      //
      // Selecting the lead entry routes through SEARCH_VALUE_SENTINEL so the typed text
      // (already mirrored to state via onInput) is committed and the popover closes.
      if (value.operator === '//' || value.operator === '!//') {
        const isMatch = value.operator === '//';
        const regex = displayValue;
        const stripped = regex.length >= 2 && regex.startsWith('/') && regex.endsWith('/') ? regex.slice(1, -1) : regex;
        const matcher = compileFilterRegex(stripped);
        // For the positive-match operator (`//`) a regex match means the row WOULD be
        // included → green check. For the negative-match operator (`!//`) a regex match
        // means the row WOULD be excluded → red minus-circle.
        const matchIconName = isMatch ? ('check-circle' as const) : ('minus-circle' as const);
        const matchIconTone = isMatch ? ('success' as const) : ('danger' as const);
        // Build matched and unmatched preview rows separately, then concatenate so all
        // matches surface at the top of the Preview section while unmatched rows keep
        // the original sample-value order.
        const matchedPreview: Array<{
          value: string;
          displayName: string;
          category: string;
          disabled: boolean;
          trailingIconName?: typeof matchIconName;
          trailingIconTone?: typeof matchIconTone;
        }> = [];
        const unmatchedPreview: typeof matchedPreview = [];
        for (const { value: v, displayValue: dv } of fieldData?.sampleValues ?? []) {
          const stringValue = String(v);
          const matches = matcher ? matcher.test(stringValue) : false;
          const row = {
            value: stringValue,
            displayName: dv ?? stringValue,
            category: msg('queryBuilder.autocomplete.previewCategory'),
            disabled: true,
            ...(matches ? { trailingIconName: matchIconName, trailingIconTone: matchIconTone } : {}),
          };
          (matches ? matchedPreview : unmatchedPreview).push(row);
        }
        const previewOptions = [...matchedPreview, ...unmatchedPreview];
        const leadPrefix = isMatch
          ? msg('queryBuilder.filterToken.regexMatchPrefix')
          : msg('queryBuilder.filterToken.regexExcludePrefix');
        return [
          {
            value: SEARCH_VALUE_SENTINEL,
            // Plain text for accessibility / search highlighting; labelNode renders the
            // regex wrapped in <code> so it visually reads as a literal pattern.
            displayName: `${leadPrefix} ${regex}`,
            labelNode: (
              <>
                {leadPrefix} <code>{regex}</code>
              </>
            ),
            description: isMatch
              ? msg('queryBuilder.filterToken.regexIncludedDescription')
              : msg('queryBuilder.filterToken.regexExcludedDescription'),
          },
          ...previewOptions,
        ];
      }

      const isInPresentMode = displayValue === PRESENT_VALUE_SENTINEL;

      // Pinned at the top when the cursor segment is non-empty — lets the user commit
      // arbitrary text as the value even when no sample option matches. Wrap the typed
      // value in double quotes when the field is a string type so the user can visually
      // tell the boundary (especially for values containing whitespace or delimiters).
      // Number fields swap the noun to "number" since the input only accepts digits.
      let isStringType = false;
      if (fieldData?.valueType) {
        isStringType = fieldData.valueType === 'string';
      } else if (fieldData?.sampleValues && fieldData.sampleValues.length > 0) {
        isStringType = inferFieldType(fieldData.sampleValues) === 'STRING';
      }
      const searchValueDisplay = isDurationField
        ? msg('queryBuilder.filterToken.searchForDuration', { text: valueSearchText })
        : isNumberField
          ? msg('queryBuilder.filterToken.searchForNumber', { text: valueSearchText })
          : isStringType
            ? msg('queryBuilder.filterToken.searchForQuotedValue', { text: valueSearchText })
            : msg('queryBuilder.filterToken.searchForValue', { text: valueSearchText });
      // The pinned entry's whole purpose is to commit the *typed* value when no
      // sample option matches — so it only makes sense when the value cell is
      const searchValueOption =
        valueSearchText && !isInPresentMode && allowCustomValue
          ? [{ value: SEARCH_VALUE_SENTINEL, displayName: searchValueDisplay }]
          : [];

      const valueOptions = valueSampleValues.map(({ value: v, approxCount, displayValue: dv }) => ({
        value: v,
        displayName: dv,
        resultsCount: approxCount,
      }));

      if (fieldAllowsMultiselect) {
        // Include values from the session that aren't in sampleValues so deselected options
        // remain available in the autocomplete for reapplication.
        const sampleSet = new Set(valueOptions.map((o) => String(o.value)));
        const sessionOnlyOpts = multiSessionOptions
          .filter((v) => !sampleSet.has(v) && v !== PRESENT_VALUE_SENTINEL)
          .map((v) => ({
            value: v,
            displayName: undefined as string | undefined,
            resultsCount: undefined as number | undefined,
          }));
        const allOptions = [...valueOptions, ...sessionOnlyOpts];
        const selectedSet = new Set(committedValues);
        const partitionSelected = <T extends { value: string | number | boolean }>(opts: T[]): T[] => {
          const selected: T[] = [];
          const unselected: T[] = [];
          for (const opt of opts) {
            (selectedSet.has(String(opt.value)) ? selected : unselected).push(opt);
          }
          return [...selected, ...unselected];
        };
        // Filter to the segment at the current cursor position, then sort. Already-selected
        // values are always kept so the user doesn't lose sight of their multi-selection while
        // narrowing the list; matching unselected values follow.
        if (valueSearchText) {
          const narrowed = isTypingValue
            ? allOptions.filter((o) => selectedSet.has(String(o.value)) || optionMatchesSearch(o, valueSearchText))
            : allOptions.slice();
          const sorted = narrowed.sort(
            (a, b) =>
              matchScore(a.displayName ?? String(a.value), valueSearchText) -
              matchScore(b.displayName ?? String(b.value), valueSearchText),
          );
          return [...searchValueOption, ...partitionSelected(sorted)];
        }
        return [...searchValueOption, ...partitionSelected(allOptions)];
      }

      if (valueSearchText) {
        // Single-value fields (boolean / context scope / comparison ops) land here. Keep the
        // committed value visible while narrowing so it isn't hidden by the typed filter.
        const selectedSet = new Set(committedValues);
        const narrowed = isTypingValue
          ? valueOptions.filter((o) => selectedSet.has(String(o.value)) || optionMatchesSearch(o, valueSearchText))
          : valueOptions.slice();
        const sorted = narrowed.sort(
          (a, b) =>
            matchScore(a.displayName ?? String(a.value), valueSearchText) -
            matchScore(b.displayName ?? String(b.value), valueSearchText),
        );
        return [...searchValueOption, ...sorted];
      }
      return [...searchValueOption, ...valueOptions];
    }

    if (activePart === 'key') {
      const keyOptions = scopedOptions.map((o) => {
        let typeLabel: string | undefined;
        if (showTypeLabels) {
          if (o.valueType) {
            typeLabel = o.valueType;
          } else if (o.sampleValues && o.sampleValues.length > 0) {
            typeLabel = inferFieldType(o.sampleValues) === 'BOOLEAN' ? 'boolean' : 'string';
          } else {
            typeLabel = 'string';
          }
        }
        return {
          value: o.field,
          displayName: o.displayValue,
          iconName: o.iconName,
          description: o.description,
          typeLabel,
        };
      });
      if (value.field) {
        // Filter to the typed text, matching the creation flow in FreeTextToken — but only
        // once the user has retyped the key. `value.field` is also the cell's live text, so
        // on a fresh open it holds the committed field name and filtering by it would hide
        // every other field; until then, sort only.
        const narrowed = hasEditedKey
          ? keyOptions.filter((o) => optionMatchesSearch(o, value.field))
          : keyOptions.slice();
        return narrowed
          .sort(
            (a, b) =>
              matchScore(a.displayName ?? String(a.value), value.field) -
              matchScore(b.displayName ?? String(b.value), value.field),
          )
          .slice(0, 10);
      }
      return keyOptions.slice(0, 10);
    }

    return [];
  }, [
    activePart,
    allowCustomValue,
    allowExistsOption,
    committedValues,
    displayValue,
    fieldAllowsMultiselect,
    fieldData,
    hasEditedKey,
    hasEditedOp,
    hasEditedValue,
    isTypingValue,
    multiSessionOptions,
    opDraft,
    scopedOptions,
    showTypeLabels,
    value.field,
    value.operator,
    valueSampleValues,
    valueSearchText,
  ]);

  // Dedupe once so the exact array the popover renders also drives keyboard
  // navigation (optionCount / handleAutocompleteSelect index). Some branches above
  // can surface the same value twice (e.g. a session-only value equal to a sample
  // value); without a shared deduped list the highlighted row and the committed
  // option would diverge.
  const popoverOptions = useMemo(() => dedupeOptionsByValue(rawPopoverOptions), [rawPopoverOptions]);

  // Metadata panel beside the open cell's autocomplete. The pinned "Search for value:" row
  // stands for an action rather than a catalog entry, so it gets no panel.
  const renderDetails = useMemo(() => {
    if (!autocompleteDetails || activePart === null || activePart === 'type') {
      return undefined;
    }
    return (option: AutocompleteOption) => {
      const raw = String(option.value);
      if (activePart === 'key') {
        const fieldOption = scopedOptions.find((o) => o.field === raw);
        return fieldOption
          ? autocompleteDetails({
              part: 'key',
              value: raw,
              displayName: option.displayName,
              field: raw,
              fieldOption,
            })
          : null;
      }
      if (activePart === 'op') {
        return autocompleteDetails({
          part: 'op',
          value: raw,
          displayName: option.displayName,
          field: value.field,
          fieldOption: fieldData,
        });
      }
      if (raw === SEARCH_VALUE_SENTINEL || raw === PRESENT_VALUE_SENTINEL) {
        return null;
      }
      return autocompleteDetails({
        part: 'value',
        value: raw,
        displayName: option.displayName,
        field: value.field,
        fieldOption: fieldData,
        resultsCount: option.resultsCount,
        operator: value.operator,
      });
    };
  }, [autocompleteDetails, activePart, scopedOptions, fieldData, value.field, value.operator]);

  const selectedValues = useMemo(() => {
    if (activePart !== 'value') {
      return [];
    }
    if (fieldAllowsMultiselect) {
      return committedValues;
    }
    if ('values' in value) {
      return (value.values ?? []).map(String);
    }
    if (Array.isArray(value.value)) {
      return value.value.map(String);
    }
    if ('value' in value && value.value !== null && value.value !== undefined) {
      return [String(value.value)];
    }
    return [];
  }, [activePart, committedValues, fieldAllowsMultiselect, value]);

  // Base list for multiselect commits. In "present" (__present__) mode selectedValues holds only
  // the sentinel, but a concrete value must REPLACE present rather than coexist with it — a filter
  // can't serialize as both "exists" and "== x". Stripping the sentinel here means appending or
  // toggling a real value drops present instead of producing a corrupt `field:=:__present__,x`.
  const concreteSelectedValues = useMemo(
    () => selectedValues.filter((v) => v !== PRESENT_VALUE_SENTINEL),
    [selectedValues],
  );

  function focusNextFreeText() {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const root = container.closest<HTMLElement>('[role="grid"], [data-qb-nav-root]') ?? wrapperRef.current;
    if (!root) {
      return;
    }
    // FreeText inputs have no data-part; lozenge cell inputs have data-part="key|op|value"
    const freeTextInputs = Array.from(root.querySelectorAll<HTMLInputElement>('input:not([data-part])'));
    const nextInput = freeTextInputs.find(
      // oxlint-disable-next-line no-bitwise
      (el) => !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING),
    );
    nextInput?.focus();
  }

  function handleRemove() {
    const container = containerRef.current;
    if (container) {
      const root = container.closest<HTMLElement>('[role="grid"], [data-qb-nav-root]') ?? wrapperRef.current;
      if (root) {
        const freeTextInputs = Array.from(root.querySelectorAll<HTMLInputElement>('input:not([data-part])'));
        const precedingInput = [...freeTextInputs].reverse().find(
          // oxlint-disable-next-line no-bitwise
          (el) => !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING),
        );
        if (precedingInput) {
          // Mark the next focus on this input as "side-effect of a remove" so
          // FreeTextToken's onFocus doesn't auto-open its autocomplete popover.
          // A subsequent user-initiated click (mousedown) will still open it.
          precedingInput.dataset.suppressAutocompleteOnFocus = '1';
          requestAnimationFrame(() => precedingInput.focus());
        } else if (variant === 'inline') {
          const preceding = getFocusableCells(root).filter(
            (el) =>
              !container.contains(el) &&
              // oxlint-disable-next-line no-bitwise
              !!(container.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING),
          );
          const target = preceding[preceding.length - 1];
          if (target) {
            requestAnimationFrame(() => target.focus());
          }
        }
      }
    }
    onRemove();
  }

  function handleAutocompleteSelect(index: number) {
    const option = popoverOptions[index];
    if (!option || ('disabled' in option && option.disabled)) {
      return;
    }
    const capturedPart = activePart;
    handleSelect(option.value);
    if (capturedPart === 'key') {
      // Unary exists has no value cell — hop to the operator picker instead.
      setActivePopover({ tokenKey, part: displayValue === PRESENT_VALUE_SENTINEL ? 'op' : 'value' });
    } else if (capturedPart === 'value' && !isMultiSelect) {
      // Move focus to the FreeText input that follows this lozenge
      focusNextFreeText();
    }
  }

  // Stable id linking the value combobox input to its popover listbox (aria-controls).
  const valueListboxId = useId();

  // Seed keyboard highlight on the current exists operator so Enter re-commits without ArrowDown.
  const operatorExistsFocusedIndex =
    activePart === 'op' && displayValue === PRESENT_VALUE_SENTINEL
      ? popoverOptions.findIndex(
          (o) => o.value === (value.operator === '!=' ? NOT_EXISTS_OPERATOR_OPTION : EXISTS_OPERATOR_OPTION),
        )
      : -1;

  const firstConcreteOpIndex = popoverOptions.findIndex((o) => !isExistsOperatorOption(String(o.value)));

  let typedSearch = '';
  if (activePart === 'key' && hasEditedKey) {
    typedSearch = value.field;
  } else if (activePart === 'op' && hasEditedOp) {
    typedSearch = opDraft.trim();
  } else if (activePart === 'value' && isTypingValue && !isRegexValueOp) {
    typedSearch = valueSearchText.trim();
  }
  const multiSelectedValues = activePart === 'value' && isMultiSelect ? new Set(valuesWithoutTypedSegment()) : null;
  const suggestedIndex = typedSearch
    ? bestMatchIndex(
        popoverOptions,
        typedSearch,
        (o) =>
          !('disabled' in o && o.disabled) &&
          o.value !== SEARCH_VALUE_SENTINEL &&
          !(activePart === 'value' && o.value === PRESENT_VALUE_SENTINEL) &&
          !multiSelectedValues?.has(String(o.value)),
      )
    : -1;

  const { focusedIndex, containerDataProps, handleKeyDown } = useAutocompleteNavigation({
    optionCount: popoverOptions.length,
    isOpen: isThisActive,
    onSelect: handleAutocompleteSelect,
    suggestedFocusedIndex: suggestedIndex >= 0 ? suggestedIndex : undefined,
    // FreeTextToken sets `autoFocusFirst` when JIT-creating a lozenge from a
    // custom field name — the user has already shown intent, so the just-opened
    // popover seeds the first *concrete* operator (skipping exists / does not exist)
    // to let Enter commit "is" and open the value cell.
    defaultFocusedIndex: activePopover?.autoFocusFirst
      ? activePart === 'op' && firstConcreteOpIndex >= 0
        ? firstConcreteOpIndex
        : 0
      : operatorExistsFocusedIndex >= 0
        ? operatorExistsFocusedIndex
        : undefined,
    onClose: () => {
      const part = activePart;
      setActivePopover(null);
      if (part) {
        requestAnimationFrame(() => {
          const btn = containerRef.current?.querySelector<HTMLElement>(`button[data-part="${part}"]`);
          // Hop focus through the lozenge's tabIndex=-1 outer container before
          // returning it to the button. The hop produces a blur → focus pair on
          // the button, which resets the wrapping TooltipTrigger's "dismissed
          // for this focus session" flag (read-only cells never unmount, so a
          // direct re-focus alone would keep the tooltip suppressed). Routing
          // through the container — which sits inside the FilterBar wrapper —
          // keeps `:focus-within` true throughout, so the FilterBar outline
          // doesn't flicker the way `btn.blur()` (focus → body) would.
          containerRef.current?.focus();
          requestAnimationFrame(() => btn?.focus());
        });
      }
    },
  });

  function operatorOptionsForCommit() {
    let allowedOps = OPERATOR_OPTIONS;
    if (fieldData?.allowedOperators) {
      allowedOps = OPERATOR_OPTIONS.filter((o) => fieldData.allowedOperators?.includes(o.value));
    } else if (fieldData?.valueType === 'string') {
      allowedOps = OPERATOR_OPTIONS.filter((o) => o.value === '=' || o.value === '!=');
    }
    return withExistsOperatorOptions(allowedOps, allowExistsOption !== false);
  }

  function resolveOperatorFromTypedLabel(typed: string): string | null {
    const trimmed = typed.trim().toLowerCase();
    if (!trimmed) {
      return null;
    }
    const optionsForCommit = operatorOptionsForCommit();
    const byDisplay = optionsForCommit.find((o) => (o.displayName ?? '').toLowerCase() === trimmed);
    if (byDisplay) {
      return String(byDisplay.value);
    }
    const byValue = optionsForCommit.find((o) => String(o.value).toLowerCase() === trimmed);
    return byValue ? String(byValue.value) : null;
  }

  function previousFreeTextFocusKey(): string {
    const flatIdx = parseInt(tokenKey, 10);
    if (Number.isNaN(flatIdx) || flatIdx <= 0) {
      return 'ft-start';
    }
    return `ft-after-${flatIdx - 1}`;
  }

  function nextFreeTextFocusKey(): string {
    const flatIdx = parseInt(tokenKey, 10);
    if (Number.isNaN(flatIdx)) {
      return 'end';
    }
    return `ft-after-${flatIdx}`;
  }

  function deleteThisFilterPill() {
    if (!parsedToken) {
      onRemove();
      setActivePopover(null);
      return;
    }
    dispatch({
      type: 'DELETE_TOKEN',
      token: parsedToken,
      focusOverride: { itemKey: previousFreeTextFocusKey(), caret: 'end' },
    });
    setActivePopover(null);
  }

  function handleValueBoundaryBackspace(e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) {
    if (e.key !== 'Backspace') {
      return;
    }
    if (e.currentTarget instanceof HTMLInputElement) {
      if (e.currentTarget.selectionStart !== 0 || e.currentTarget.selectionEnd !== 0) {
        return;
      }
    }
    e.preventDefault();
    e.stopPropagation();
    const base = hasEditedOp ? opDraft : operatorCellDisplay;
    const nextDraft = base.slice(0, -1);
    skipOpDraftResetRef.current = true;
    setOpDraft(nextDraft);
    setHasEditedOp(true);
    if (nextDraft.trim() === '') {
      setOperatorUnset(true);
    }
    if (isBooleanField && e.currentTarget instanceof HTMLInputElement && e.currentTarget.value === '') {
      setValueCleared(true);
      setBoolDraft('');
      setHasEditedBool(true);
      handleCellInput('value', '');
    }
    setActivePopover({ tokenKey, part: 'op' });
  }

  function handleOpBoundaryBackspace(e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) {
    if (e.key !== 'Backspace') {
      return;
    }
    if (e.currentTarget instanceof HTMLInputElement) {
      if (e.currentTarget.selectionStart !== 0 || e.currentTarget.selectionEnd !== 0) {
        return;
      }
    }
    e.preventDefault();
    e.stopPropagation();

    if (opDraft.length > 0) {
      setActivePopover({ tokenKey, part: 'key' });
      return;
    }

    if (!parsedToken) {
      setActivePopover({ tokenKey, part: 'key' });
      return;
    }

    const field = value.field;
    if (field.length <= 1) {
      deleteThisFilterPill();
      return;
    }

    const tokenText = parsedToken.text;
    const withoutLastKeyChar = tokenText.replace(field, field.slice(0, -1));
    const newQuery = replaceToken(query, parsedToken, withoutLastKeyChar);
    dispatch({
      type: 'SET_QUERY',
      query: newQuery,
      focusOverride: { itemKey: tokenKey, part: 'key', caret: 'end', activate: true },
    });
  }

  function handleKeyBoundaryBackspace(e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) {
    if (e.key !== 'Backspace') {
      return;
    }

    if (e.currentTarget instanceof HTMLInputElement) {
      const input = e.currentTarget;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      if (start === 0 && end === 0 && input.value.length > 0) {
        return;
      }
      const next =
        start === end
          ? `${input.value.slice(0, Math.max(0, start - 1))}${input.value.slice(end)}`
          : `${input.value.slice(0, start)}${input.value.slice(end)}`;
      if (next.length > 0) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      deleteThisFilterPill();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    if (!parsedToken) {
      onRemove();
      return;
    }
    const field = value.field;
    if (field.length <= 1) {
      deleteThisFilterPill();
      return;
    }
    const tokenText = parsedToken.text;
    const withoutLastKeyChar = tokenText.replace(field, field.slice(0, -1));
    const newQuery = replaceToken(query, parsedToken, withoutLastKeyChar);
    dispatch({
      type: 'SET_QUERY',
      query: newQuery,
      focusOverride: { itemKey: tokenKey, part: 'key', caret: 'end', activate: true },
    });
  }

  function handleOpTextInput(text: string) {
    setHasEditedOp(true);
    setOpDraft(text);
    if (text.trim() === '') {
      setOperatorUnset(true);
    }
    if (!/\s$/.test(text)) {
      return;
    }
    const matched = resolveOperatorFromTypedLabel(text);
    if (matched === null) {
      return;
    }
    handleSelect(matched);
  }

  function handleBooleanValueInput(text: string) {
    setHasEditedValue(true);
    setHasEditedBool(true);
    setBoolDraft(text);
    setCursorSegment(text);
    setCursorSegmentIndex(0);
    setEditedSegmentIndex(0);
    if (text === 'true' || text === 'false') {
      setValueCleared(false);
      handleCellInput('value', text);
      return;
    }
    if (text === '') {
      setValueCleared(true);
      handleCellInput('value', '');
    }
  }

  if (variant === 'inline') {
    const inlineIcon = icon ?? fieldData?.iconName;
    const inlineValues = inlineValuesFor(value, parsedToken);
    const inlineDisplayValues = inlineValues.map((v) => sampleDisplayLabel(String(v)));
    const inlineFieldLabel = fieldData?.displayValue ?? value.field;
    const customInlineLabel = fieldData?.renderTriggerLabel
      ? fieldData.renderTriggerLabel({ operator: value.operator, values: inlineValues, fieldOption: fieldData })
      : undefined;
    const inlineLabelParts: { primary: ReactNode; overflow?: string } = customInlineLabel
      ? { primary: customInlineLabel }
      : buildButtonLabelParts(inlineDisplayValues, `Any ${inlineFieldLabel}`, inlineFieldLabel, {
          truncateSingleValue,
        });
    const inlineTooltip = inlineValueTooltip(isBooleanField, inlineDisplayValues);

    const commitInlineValues = (values: Array<string | number | boolean>) => {
      onEditValues?.(values);
      setInlineValueOpen(false);
    };

    const toggleBoolean = () => {
      const current = inlineValues.length > 0 ? String(inlineValues[0]) : undefined;
      onEditValues?.([current === 'true' ? 'false' : 'true']);
    };

    return (
      // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper only stops click propagation; the controls inside are interactive
      <div
        ref={containerRef}
        className={clsx(styles.container, styles.inlineContainer)}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div {...containerProps} className={styles.lozengeFilter}>
          <div {...cellProps}>
            <TooltipTrigger delay={500}>
              <Button
                ref={inlineValueTriggerRef}
                className={clsx(styles.inlineValueCell, { [styles.cellFocused]: inlineValueOpen })}
                data-part="value"
                aria-label={
                  isBooleanField
                    ? msg('queryBuilder.filterToken.toggleLabel', { field: value.field })
                    : msg('queryBuilder.filterToken.editValuesLabel', { field: value.field })
                }
                aria-haspopup={isBooleanField ? undefined : 'dialog'}
                aria-expanded={isBooleanField ? undefined : inlineValueOpen}
                isDisabled={!fieldData}
                onPress={() => {
                  if (isBooleanField) {
                    toggleBoolean();
                    return;
                  }
                  if (Date.now() - inlineValueLastCloseAtRef.current < 150) {
                    return;
                  }
                  setInlineValueOpen((open) => !open);
                }}
              >
                {!fieldData?.renderTriggerLabel && inlineIcon && <Icon name={inlineIcon} size="small" />}
                <span className={styles.cellContent}>
                  <span className={styles.inlineValueLabel}>{inlineLabelParts.primary}</span>
                  {inlineLabelParts.overflow ? (
                    <span className={styles.cellLabelOverflow}>{` ${inlineLabelParts.overflow}`}</span>
                  ) : null}
                </span>
              </Button>
              <Tooltip>{inlineTooltip}</Tooltip>
            </TooltipTrigger>
            {fieldData && !isBooleanField && (
              <Popover
                triggerRef={inlineValueTriggerRef}
                isOpen={inlineValueOpen}
                onOpenChange={(open) => {
                  setInlineValueOpen(open);
                  if (!open) {
                    inlineValueLastCloseAtRef.current = Date.now();
                  }
                }}
                placement="bottom start"
                className={styles.inlineValuePopover}
                aria-label={msg('queryBuilder.filterToken.editValuesPopoverLabel', { field: value.field })}
              >
                <FieldValuePanel
                  field={fieldData}
                  initialValues={inlineValues}
                  immediate
                  onValuesChange={(values) => onEditValues?.(values)}
                  onApply={commitInlineValues}
                  onClose={() => setInlineValueOpen(false)}
                  onDismiss={() => setInlineValueOpen(false)}
                />
              </Popover>
            )}
          </div>
          {removable && (
            <div {...cellProps}>
              <TooltipTrigger delay={500}>
                <IconButton
                  className={styles.removeButton}
                  icon="cancel"
                  variant="minimal"
                  size="small"
                  aria-label={msg('queryBuilder.filterToken.removeFilterLabel', { field: value.field })}
                  onPress={handleRemove}
                  isDisabled={readonly}
                />
                <Tooltip>
                  <Message id="queryBuilder.filterToken.removeFilter" />
                </Tooltip>
              </TooltipTrigger>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper exists only to stop click propagation; interactive controls live inside
    <div
      ref={containerRef}
      className={styles.container}
      // tabIndex=-1 lets us programmatically focus this element during the
      // Escape close path (see `useAutocompleteNavigation.onClose` below) so
      // we can briefly hop focus off the trigger button without the FilterBar
      // wrapper losing its `:focus-within` outline.
      tabIndex={-1}
      onFocus={() => setLozengeFocused(true)}
      onBlur={(e) => {
        const next = e.relatedTarget;
        if (next instanceof Node && e.currentTarget.contains(next)) {
          return;
        }
        setLozengeFocused(false);
      }}
      onPointerDownCapture={(e) => {
        const part = (e.target as Element).closest<HTMLElement>('[data-part]')?.dataset.part;
        const cellPart = part === 'key' || part === 'op' || part === 'value' ? part : null;
        pointerDownPartRef.current = cellPart;
        cellPointerGestureRef.current = cellPart;
        // Commit the cell switch on pointerdown — before the value <input> unmount shifts
        // layout under the still-down pointer — so pointerup/click keep the intended cell
        // and React Aria's interact-outside cannot clear the handoff.
        if (cellPart && !readonly && isThisActive && activePopover?.part !== cellPart) {
          // Keep skipSamePartToggle until the finishing click (do not clear on setTimeout(0) —
          // that timer can fire between pointerdown and click and let the click toggle-close).
          skipSamePartToggleRef.current = cellPart;
          setActivePopover({ tokenKey, part: cellPart });
        }
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Wrapping the cell input and the popover's ListBox under one react-aria Autocomplete gives the */}
      {/* ListBox virtual focus, so async-loaded value suggestions no longer pull DOM focus off the input. */}
      {/* disableAutoFocusFirst + no `filter` keep react-aria's nav/filtering inert; useAutocompleteNavigation */}
      {/* and the app-side option filtering stay authoritative. */}
      <Autocomplete
        inputValue={
          activePart === 'key'
            ? value.field
            : activePart === 'op'
              ? popoverOpDraft !== undefined && !hasEditedOp
                ? popoverOpDraft
                : hasEditedOp
                  ? opDraft
                  : autoFocusFirstOp
                    ? ''
                    : operatorCellDisplay
              : valueCellInputText
        }
        onInputChange={() => {}}
        disableAutoFocusFirst
      >
        {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- role="row" is provided via containerProps spread */}
        <div
          {...containerProps}
          {...containerDataProps}
          onKeyDown={isThisActive ? handleKeyDown : undefined}
          onKeyDownCapture={(e) => {
            containerProps.onKeyDownCapture?.(e);
            if (!isThisActive) {
              return;
            }

            if (activePart === 'value') {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                suppressNextPopoverCloseCommit();
                if (focusedIndex >= 0) {
                  handleAutocompleteSelect(focusedIndex);
                  if (isMultiSelect) {
                    setActivePopover(null);
                    focusAfterValueLozenge();
                  }
                } else {
                  setActivePopover(null);
                  focusAfterValueLozenge();
                }
                requestAnimationFrame(() => {
                  dispatch({ type: 'COMMIT_QUERY', force: true });
                });
                return;
              }
              if (e.key === 'Tab' && !e.shiftKey && focusedIndex >= 0) {
                e.preventDefault();
                e.stopPropagation();
                if (!isMultiSelect) {
                  suppressNextPopoverCloseCommit();
                }
                handleAutocompleteSelect(focusedIndex);
                return;
              }
              return;
            }

            const acceptIndex = (e.key === 'Tab' && !e.shiftKey) || e.key === 'Enter' ? focusedIndex : -1;
            if (acceptIndex >= 0) {
              e.preventDefault();
              e.stopPropagation();
              handleAutocompleteSelect(acceptIndex);
            }
          }}
          className={styles.lozengeFilter}
        >
          {icon && (
            <div {...cellProps}>
              <Icon name={icon} size="small" className={styles.icon} />
            </div>
          )}

          {/* Key cell */}
          <div {...cellProps}>
            <EditableCell
              ref={keyRef}
              data-part="key"
              aria-label={msg('queryBuilder.filterToken.editFieldLabel', { field: value.field })}
              value={value.field}
              displayValue={fieldData?.displayValue ?? (!allowCustomKey ? value.field : undefined)}
              tooltip={msg('queryBuilder.filterToken.editField')}
              isActivePrev={activePart === 'op'}
              isActive={activePart === 'key'}
              isActiveNext={false}
              readOnly={!allowCustomKey}
              showQuotes
              onClick={() => handleCellPress('key')}
              onDeactivate={(related) => handleCellDeactivate(related, 'key')}
              onInput={(e) => handleCellInput('key', e.currentTarget.value)}
              onInputKeyDown={handleKeyBoundaryBackspace}
              disabled={readonly}
            />
          </div>

          {/* Operator cell. Exists / does not exist are unary operators, so this cell
              stays visible and the value cell is hidden for the present sentinel.
              Editable (not readOnly) so ArrowLeft/Right, Alt+Arrow, and Backspace can
              walk the operator label character/word-wise like the value and key cells. */}
          <div {...cellProps}>
            <EditableCell
              ref={opRef}
              data-part="op"
              aria-label={msg('queryBuilder.filterToken.editOperatorLabel', { field: value.field })}
              value={operatorUnset ? '' : operatorCellDisplay}
              initialInputValue={
                popoverOpDraft !== undefined && !hasEditedOp
                  ? popoverOpDraft
                  : operatorUnset
                    ? hasEditedOp
                      ? opDraft
                      : ''
                    : hasEditedOp
                      ? opDraft
                      : autoFocusFirstOp
                        ? ''
                        : operatorCellDisplay
              }
              displayValue={operatorUnset ? '' : operatorCellDisplay}
              incomplete={operatorUnset && activePart !== 'op'}
              tooltip={msg('queryBuilder.filterToken.editOperator')}
              isActivePrev={displayValue !== PRESENT_VALUE_SENTINEL && activePart === 'value'}
              isActive={activePart === 'op'}
              isActiveNext={activePart === 'key'}
              onClick={() => handleCellPress('op')}
              onDeactivate={(related) => handleCellDeactivate(related, 'op')}
              onInput={(e) => handleOpTextInput(e.currentTarget.value)}
              onInputKeyDown={handleOpBoundaryBackspace}
              disabled={readonly}
            />
          </div>

          {/* Value cell — omitted for exists / does not exist (unary). */}
          {displayValue !== PRESENT_VALUE_SENTINEL && (
            <div {...cellProps}>
              {(() => {
                // Regex tokens are stored with literal `/` delimiters (e.g. `/^foo/`). The
                // delimiters belong to the syntax, not the regex content the user is editing —
                // so strip them for the editable input/button display and surface them as fixed
                // affixes on either side via EditableCell's `prefix`/`suffix` props. The onInput
                // handler re-wraps the typed content with `/` before dispatching so the stored
                // token shape stays consistent.
                const isRegexOp = value.operator === '//' || value.operator === '!//';
                const stripSlashes = (s: string): string =>
                  s.length >= 2 && s.startsWith('/') && s.endsWith('/') ? s.slice(1, -1) : s;
                const cellValue = isRegexOp
                  ? stripSlashes(displayValue)
                  : multiValueParts
                    ? multiValueParts.join(', ')
                    : showClearedBoolean
                      ? boolDraft
                      : displayValue;
                const cellInitialInputValue = isBooleanField
                  ? hasEditedBool
                    ? boolDraft
                    : displayValue === PRESENT_VALUE_SENTINEL
                      ? ''
                      : displayValue
                  : isRegexOp
                    ? stripSlashes(displayValue)
                    : fieldAllowsMultiselect && displayValue
                      ? `${multiValueParts?.join(', ') ?? displayValue}, `
                      : undefined;
                const cellDisplayValue = showClearedBoolean
                  ? undefined
                  : isRegexOp
                    ? stripSlashes(displayValue)
                    : multiValueButtonLabel
                      ? multiValueButtonLabel
                      : !allowCustomValue && displayValue
                        ? sampleDisplayLabel(displayValue)
                        : undefined;
                return (
                  <EditableCell
                    ref={valueRef}
                    data-part="value"
                    aria-label={msg('queryBuilder.filterToken.editValueLabel', { field: value.field })}
                    value={cellValue}
                    initialInputValue={cellInitialInputValue}
                    displayValue={cellDisplayValue}
                    overflowSuffix={isRegexOp ? undefined : multiValueOverflowSuffix}
                    tooltip={msg('queryBuilder.filterToken.editValue')}
                    prefix={isRegexOp ? '/' : undefined}
                    suffix={isRegexOp ? '/' : undefined}
                    trailingIcon={undefined}
                    isActivePrev={false}
                    isActive={activePart === 'value'}
                    isActiveNext={activePart === 'op'}
                    readOnly={valueCellReadOnly}
                    numberOnly={isNumberField && !isRegexOp}
                    durationUnits={isDurationField && !isRegexOp}
                    comboboxListboxId={valueListboxId}
                    comboboxExpanded={isThisActive && activePart === 'value'}
                    incomplete={
                      cellValue === '' && (!focusInsideLozenge || (showClearedBoolean && activePart !== 'value'))
                    }
                    onClick={() => handleCellPress('value')}
                    onDeactivate={(related) => handleCellDeactivate(related, 'value')}
                    onInputKeyDown={handleValueBoundaryBackspace}
                    onInput={(e) => {
                      const el = e.currentTarget;
                      const text = el.value;
                      if (!hasEditedValue && /^(?:=|!=|>=|<=|>|<)$/.test(text)) {
                        el.value = '';
                        return;
                      }
                      if (isBooleanField) {
                        handleBooleanValueInput(text);
                        return;
                      }
                      // Update cursor segment so autocomplete filters by what's at the cursor.
                      const pos = el.selectionStart ?? text.length;
                      const segmentIndex = getSegmentIndexAtCursor(text, pos);
                      setCursorSegment(getSegmentAtCursor(text, pos));
                      setCursorSegmentIndex(segmentIndex);
                      setEditedSegmentIndex(segmentIndex);
                      setHasEditedValue(true);
                      if (isRegexOp) {
                        // Re-wrap the typed regex content in the literal `/` delimiters before
                        // dispatching, so the stored value keeps its slash-wrapped shape.
                        handleCellInput('value', `/${text}/`);
                      } else if (fieldAllowsMultiselect) {
                        // Commit the parsed values as an ARRAY (not a comma-joined string) so the
                        // round-trip through the query string preserves individual values. Do NOT
                        // update multiSessionOptions here — partial words typed by the user should not
                        // become persistent options; only explicit autocomplete selections are tracked.
                        commitFilterValue(parseMultiValue(text));
                      } else {
                        handleCellInput('value', text);
                      }
                    }}
                    disabled={readonly}
                  />
                );
              })()}
            </div>
          )}

          {removable && (
            <div {...cellProps}>
              <TooltipTrigger delay={500}>
                <IconButton
                  className={styles.removeButton}
                  icon="cancel"
                  variant="minimal"
                  size="small"
                  aria-label={msg('queryBuilder.filterToken.removeFilterLabel', { field: value.field })}
                  onPress={handleRemove}
                  isDisabled={readonly}
                />
                <Tooltip>
                  <Message id="queryBuilder.filterToken.removeFilter" />
                </Tooltip>
              </TooltipTrigger>
            </div>
          )}
        </div>

        {isThisActive && (
          <AutocompletePopover
            id={valueListboxId}
            triggerRef={activeTriggerRef}
            anchorKey={`${tokenKey}-${activePart ?? 'key'}`}
            isOpen
            onOpenChange={(open) => {
              if (open) {
                return;
              }
              // Sibling cells sit outside the popover portal. React Aria's interact-outside
              // fires on pointerup for those presses and would clear activePopover after
              // handleCellDeactivate already moved it to the pressed cell — keep the handoff.
              if (cellPointerGestureRef.current !== null) {
                return;
              }
              setActivePopover(null);
            }}
            onSelect={handleSelect}
            options={popoverOptions}
            isLoading={activePart === 'value' && fieldData?.isLoadingValues}
            // When the value cell is hosting a regex op, the popover renders the lead
            // selectable option in an unnamed section above a "Preview" section
            // containing one disabled row per sample value (matches get a green check).
            groupByCategory={activePart === 'value' && isRegexValueOp}
            // Interactive multiselect is FIELD-gated via isMultiSelect (never a boolean, the context
            // scope field, or a comparison op).
            multiSelect={isMultiSelect}
            selectedKeys={isMultiSelect ? selectedValues : undefined}
            focusedIndex={focusedIndex}
            searchText={
              activePart === 'key'
                ? value.field
                : activePart === 'op'
                  ? hasEditedOp
                    ? opDraft
                    : undefined
                  : activePart === 'value' && !isRegexValueOp && isTypingValue
                    ? valueSearchText
                    : undefined
            }
            renderDetails={renderDetails}
            footer={autocompleteFooter}
          />
        )}
      </Autocomplete>
    </div>
  );
}

LozengeFilter.displayName = 'LozengeFilter';

/**
 * Returns true when the key text is safe to dispatch.
 *
 * Keys with spaces are allowed — stringifyToken will auto-quote them.
 * Bare (unquoted) keys must still satisfy the grammar's identifier rule so
 * that a trailing period or other invalid char doesn't produce an unparseable
 * query string.  Empty keys are always rejected.
 */
function isValidKey(text: string): boolean {
  if (!text) {
    return false;
  }
  if (/\s/.test(text)) {
    return true;
  } // will be auto-quoted by stringifyToken
  return /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*$/.test(text);
}
